// apolo/src/lib/review/checksRunner.js — harness-uplift-r2 E6.
// @arch:pure-engine
//
// Amp Checks: user-defined review criteria live in `.apolo/checks/*.md` (frontmatter
// name/description/severity). During review, ONE subagent is planned per check,
// scoped by directory — the check nearest a changed file wins over a homonym higher
// up. This is the PURE PLANNER: it parses + scopes checks and returns the plan
// (read-only; it never executes a subagent). Never throws.
//
// Anti-fan-out note (Cognition principle 2): planned checks are READ-ONLY reviews
// that each return a summary — they never write code in parallel with coupled
// decisions, so this stays on the safe side of the multi-agent tension.

const SEVERITIES = ['low', 'medium', 'high', 'critical'];

/** Parse a check file's frontmatter (name/description/severity). Pure. */
export function parseCheck(file) {
  try {
    if (!file || typeof file !== 'object') return null;
    const content = typeof file.content === 'string' ? file.content : '';
    const dir = typeof file.dir === 'string' ? file.dir : '';
    const get = (key) => {
      const m = content.match(new RegExp(`^${key}:\\s*(.+)$`, 'im'));
      return m ? m[1].trim() : '';
    };
    const name = get('name') || (typeof file.name === 'string' ? file.name : '');
    if (!name) return null;
    const sevRaw = get('severity').toLowerCase();
    const severity = SEVERITIES.includes(sevRaw) ? sevRaw : 'medium';
    return { name, description: get('description'), severity, dir };
  } catch {
    return null;
  }
}

/** True when `dir` scopes `filePath` (filePath is under dir, or dir is repo-root ''). */
function scopes(dir, filePath) {
  if (!dir) return true; // global check
  const d = dir.replace(/\/+$/, '');
  return String(filePath || '').startsWith(`${d}/`);
}

/**
 * Plan one check per criterion applicable to the changed files. When two checks
 * share a name, the one whose dir is DEEPER (nearest the file) wins.
 *
 * @param {Array<{content?:string,dir?:string,name?:string}>} checkFiles
 * @param {{ changedPaths?: string[] }} [opts]
 * @returns {Array<{name:string,description:string,severity:string,dir:string}>}
 */
export function planChecks(checkFiles, opts = {}) {
  try {
    if (!Array.isArray(checkFiles)) return [];
    const changed = Array.isArray(opts.changedPaths) ? opts.changedPaths : [];
    const parsed = checkFiles.map(parseCheck).filter(Boolean);

    // Keep checks that scope at least one changed file (or all when no changed set).
    const applicable = parsed.filter((c) => changed.length === 0 || changed.some((p) => scopes(c.dir, p)));

    // Nearest-wins dedup by name: deeper dir overrides a homonym.
    const byName = new Map();
    for (const c of applicable) {
      const prev = byName.get(c.name);
      if (!prev || c.dir.length > prev.dir.length) byName.set(c.name, c);
    }
    return [...byName.values()];
  } catch {
    return [];
  }
}
